import type { ReimbursementMilestoneId } from '../types/reimbursementFormTypes'

export type ReimbursementSectionKey = 'core' | 'unique' | 'final'

export type ReimbursementSectionStatusValue =
  | 'DRAFT'
  | 'SUBMITTED'
  | 'APPROVED'
  | 'UNLOCKED'
  | null

export const MILESTONE_TO_SECTION: Record<ReimbursementMilestoneId, ReimbursementSectionKey> = {
  1: 'core',
  2: 'unique',
  3: 'final',
}

const SECTION_STATUS_ATTR: Record<ReimbursementMilestoneId, string> = {
  1: 'core_section_status',
  2: 'unique_section_status',
  3: 'final_section_status',
}

export function parseReimbursementSectionStatus(raw: unknown): ReimbursementSectionStatusValue {
  const normalized = String(raw ?? '').trim().toUpperCase()
  if (!normalized) return null
  if (
    normalized === 'DRAFT' ||
    normalized === 'SUBMITTED' ||
    normalized === 'APPROVED' ||
    normalized === 'UNLOCKED'
  ) {
    return normalized
  }
  return null
}

export function getSectionStatusForMilestone(
  residentDefault: Record<string, unknown> | null | undefined,
  milestoneId: ReimbursementMilestoneId,
): ReimbursementSectionStatusValue {
  if (!residentDefault) return null
  const key = SECTION_STATUS_ATTR[milestoneId]
  return parseReimbursementSectionStatus(residentDefault[key])
}

/** Read workflow status from legacy `resident_default` and decoupled `reimbursement_detail`. */
export function resolveSectionWorkflowStatus(
  residentDefault: Record<string, unknown> | null | undefined,
  reimbursementDetail: Record<string, unknown> | null | undefined,
  milestoneId: ReimbursementMilestoneId,
): ReimbursementSectionStatusValue {
  const key = SECTION_STATUS_ATTR[milestoneId]
  return (
    parseReimbursementSectionStatus(residentDefault?.[key]) ??
    parseReimbursementSectionStatus(reimbursementDetail?.[key]) ??
    null
  )
}

export function isPmcSectionFormDisabled(
  status: ReimbursementSectionStatusValue,
  isPmcRole: boolean,
): boolean {
  if (!isPmcRole) return false
  return status === 'SUBMITTED' || status === 'APPROVED'
}

export function isSectionSaveDisabled(status: ReimbursementSectionStatusValue): boolean {
  return status === 'SUBMITTED' || status === 'APPROVED'
}

/** PMC Save as Draft / Submit — hidden after Liberty approves the section (`Submitted · Approved`). */
export function shouldShowPmcSectionSaveSubmitActions(
  status: ReimbursementSectionStatusValue,
): boolean {
  return status !== 'APPROVED'
}

export function isSectionSubmitDisabled(
  status: ReimbursementSectionStatusValue,
  milestoneComplete: boolean,
): boolean {
  if (status === 'SUBMITTED' || status === 'APPROVED') return true
  return !milestoneComplete
}

export function isSectionUnlockDisabled(
  status: ReimbursementSectionStatusValue,
  isLibertyRole: boolean,
): boolean {
  if (!isLibertyRole) return true
  return status !== 'SUBMITTED'
}

/** Liberty Unlock shown when section is SUBMITTED (PMC locked). */
export function isSectionUnlockedForLiberty(status: ReimbursementSectionStatusValue): boolean {
  return status !== 'SUBMITTED' && status !== 'APPROVED'
}

/**
 * PMC always uses Save/Submit. Liberty uses Save/Submit while the section is editable,
 * and Unlock only after PMC (or Liberty on their behalf) has submitted the section.
 */
export function getReimbursementSectionActionMode(
  isLibertyRole: boolean,
  sectionWorkflowStatus: ReimbursementSectionStatusValue,
): 'pmc' | 'liberty' {
  if (!isLibertyRole) return 'pmc'
  return sectionWorkflowStatus === 'SUBMITTED' ? 'liberty' : 'pmc'
}

export function areAllReimbursementSectionsApproved(
  sectionWorkflowStatus: Partial<Record<1 | 2 | 3, ReimbursementSectionStatusValue>>,
): boolean {
  return (
    sectionWorkflowStatus[1] === 'APPROVED' &&
    sectionWorkflowStatus[2] === 'APPROVED' &&
    sectionWorkflowStatus[3] === 'APPROVED'
  )
}

export function sectionStatusDisplayLabel(status: ReimbursementSectionStatusValue): string {
  switch (status) {
    case 'SUBMITTED':
      return 'Submitted · Awaiting approval'
    case 'APPROVED':
      return 'Submitted · Approved'
    case 'DRAFT':
    case 'UNLOCKED':
    case null:
    default:
      return 'In progress'
  }
}
